import React, { useState, useEffect, useRef } from "react";
import styled, { css } from "styled-components";
import { Player, GameRole } from "../../types";
import { dictionaries } from "../../utils/dictionary";
import GameActions from "./GameActions";
import PlayerPunishment from "./PlayerPunishment";
import NightActions from "./NightActions";
import PhaseHeader from "./PhaseHeader";

interface NightPhaseProps {
  language: number;
  players: Player[];
  playerRoles: GameRole[];
  judge?: Player;
  playersLeaved: number[];
  playerFauls: number[];
  nightCount: number;
  aliveDon: number;
  aliveSheriff: number;
  handledFouls: Set<string>;
  streamError?: boolean;
  onMorning: (nightActions: NightActionsData) => void;
  onPlayerPunish: (playerIndex: number) => void;
  onPlayerFoulAction: (
    playerIndex: number,
    action:
      | "add"
      | "reset"
      | "disqualify"
      | "penalty-today"
      | "penalty-tomorrow"
  ) => void;
  onMarkFoulAsHandled: (playerIndex: number, foulCount: number) => void;
  onDisqualifyPlayer: (playerIndex: number) => void;
  onPPK: (playerIndex: number, winningTeam: "red" | "black") => void;
  onBackToDay: () => void;
  onTimeoutToggle: () => void;
  onShowRoles: () => void;
  onCancel: () => void;
  // Timer state
  timerAccumulated?: number;
  timerStartedAt?: number;
  onTimerStateChange?: (accumulated: number, startedAt: number | undefined) => void;
}

interface NightActionsData {
  shooting?: number;
  mafiaTarget: boolean;
  donCheck?: number;
  sheriffCheck?: number;
  donMissed: boolean;
  sheriffMissed: boolean;
}

const PhaseBackground = styled.div`
  position: relative;
  min-height: 100vh;
  padding-bottom: 4rem;
  background: radial-gradient(
      circle at top,
      rgba(78, 51, 152, 0.3),
      transparent 55%
    ),
    radial-gradient(
      circle at bottom right,
      rgba(18, 12, 36, 0.72),
      transparent 60%
    ),
    #05030c;
  color: #efeaff;
`;

const PhaseContainer = styled.div`
  position: relative;
  max-width: 1200px;
  margin: 0 auto;
  padding: 1.5rem 2rem 3rem;

  @media (max-width: 992px) {
    padding: 1.5rem 1.5rem 2.5rem;
  }

  @media (max-width: 768px) {
    padding: 1rem 1.25rem 2rem;
  }
`;

const PhaseMain = styled.div.attrs({ className: "main" })`
  margin-top: 1.5rem;

  @media (max-width: 768px) {
    margin-top: 1rem !important;
  }
`;

const PhaseStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const SectionCard = styled.div`
  padding: 1.35rem 1.5rem;
  border-radius: 22px;
  background: rgba(12, 8, 26, 0.94);
  border: 1px solid rgba(124, 93, 255, 0.22);
  box-shadow: 0 16px 48px rgba(5, 3, 16, 0.45);
`;

const MetaCard = styled(SectionCard)`
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.12);
  color: rgba(239, 234, 255, 0.75);
`;

const PlayersTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: rgba(12, 8, 26, 0.96);
  border-radius: 22px;
  overflow: hidden;
  box-shadow: 0 16px 48px rgba(5, 3, 16, 0.45);

  thead th {
    padding: 1rem 1.25rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    font-size: 0.75rem;
    font-weight: 700;
    background: rgba(255, 255, 255, 0.08);
    color: rgba(239, 234, 255, 0.82);
  }

  tbody tr {
    transition: background 0.15s ease-out;
  }

  tbody tr:nth-child(even) {
    background: rgba(255, 255, 255, 0.02);
  }

  tbody td {
    padding: 0.65rem 1rem;
    border-top: 1px solid rgba(255, 255, 255, 0.06);
    vertical-align: middle;

    @media (max-width: 768px) {
      padding: 0.6rem 0.5rem;
    }
  }
`;

const RolesToggle = styled.button.attrs({ type: "button" })`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.4rem 0.75rem;
  border-radius: 12px;
  background: rgba(124, 93, 255, 0.15);
  border: 1px solid rgba(124, 93, 255, 0.35);
  color: #d6d0ff;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  cursor: pointer;
  transition: background 0.15s ease-out, transform 0.15s ease-out;

  &:hover {
    background: rgba(124, 93, 255, 0.25);
    transform: translateY(-1px);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const PlayerRow = styled.tr`
  &.dead {
    opacity: 0.3;
  }
`;

const NumberCell = styled.td`
  width: 56px;
  font-weight: 700;
  font-size: 0.95rem;
`;

const FoulCell = styled.td`
  width: 80px;
`;

const PlayerInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const PlayerAvatar = styled.img`
  width: 42px;
  height: 42px;
  border-radius: 14px;
  object-fit: cover;
  border: 2px solid rgba(255, 255, 255, 0.12);
`;

const PlayerName = styled.span`
  font-weight: 600;
  font-size: 1rem;
`;

const roleTagStyles = {
  red: css`
    background: rgba(0, 186, 255, 0.16);
    border: 1px solid rgba(0, 186, 255, 0.45);
    color: #bde9ff;
  `,
  black: css`
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.16);
    color: rgba(239, 234, 255, 0.8);
  `,
  don: css`
    background: rgba(124, 93, 255, 0.16);
    border: 1px solid rgba(124, 93, 255, 0.42);
    color: #d6d0ff;
  `,
  sheriff: css`
    background: rgba(255, 196, 0, 0.16);
    border: 1px solid rgba(255, 196, 0, 0.42);
    color: #ffe8a6;
  `,
  none: css`
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.16);
    color: rgba(239, 234, 255, 0.8);
  `,
} as const;

type RoleVariant = keyof typeof roleTagStyles;

const RoleTag = styled.span<{ $variant: RoleVariant }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.2rem 0.65rem;
  border-radius: 999px;
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-weight: 700;
  ${({ $variant }) => roleTagStyles[$variant]};
`;

const FoulBadge = styled.button.attrs({ type: "button" })`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.18);
  color: #efeaff;
  font-weight: 700;
  transition: all 0.2s ease;
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.18);
    transform: translateY(-1px);
  }
`;

const buttonVariants = {
  primary: css`
    background: linear-gradient(
      135deg,
      rgba(124, 93, 255, 0.95),
      rgba(70, 46, 150, 0.95)
    );
    border: 1px solid rgba(124, 93, 255, 0.55);
  `,
  secondary: css`
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.18);
  `,
  danger: css`
    background: linear-gradient(
      135deg,
      rgba(255, 96, 122, 0.95),
      rgba(214, 38, 78, 0.95)
    );
    border: 1px solid rgba(255, 96, 122, 0.5);
  `,
  outline: css`
    background: transparent;
    border: 1px solid rgba(255, 255, 255, 0.22);
    color: #efeaff;

    &:hover {
      background: rgba(255, 255, 255, 0.08);
    }
  `,
  ghost: css`
    background: transparent;
    border: 1px solid transparent;
    color: rgba(239, 234, 255, 0.65);

    &:hover {
      color: #efeaff;
      background: rgba(255, 255, 255, 0.08);
    }
  `,
} as const;

type ButtonVariant = keyof typeof buttonVariants;

const PhaseButton = styled.button.attrs({ type: "button" })<{
  $variant?: ButtonVariant;
}>`
  border-radius: 16px;
  padding: 0.75rem 1.25rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: #efeaff;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease,
    color 0.2s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  ${({ $variant = "primary" }) => buttonVariants[$variant]};

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 14px 36px rgba(124, 93, 255, 0.32);
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
    box-shadow: none;
  }
`;

const ActionBar = styled.div`
  display: grid;
  gap: 0.75rem;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
`;

const MetaInfo = styled.div`
  text-align: center;
  font-size: 0.85rem;
  line-height: 1.6;
  color: inherit;
`;

const MetaHighlight = styled.span`
  color: #efeaff;
  font-weight: 600;
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(5, 3, 12, 0.72);
  backdrop-filter: blur(12px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  z-index: 1050;
`;

const ModalCard = styled.div`
  width: min(520px, 100%);
  background: linear-gradient(
    145deg,
    rgba(22, 14, 42, 0.96),
    rgba(12, 8, 28, 0.96)
  );
  border: 1px solid rgba(124, 93, 255, 0.22);
  border-radius: 26px;
  box-shadow: 0 28px 90px rgba(4, 2, 14, 0.65);
  color: #f5f2ff;
  overflow: hidden;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem 1.75rem 0;
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-size: 1.35rem;
  font-weight: 700;
  color: #f5f2ff;
`;

const ModalCloseButton = styled.button.attrs({ type: "button" })`
  background: none;
  border: none;
  color: rgba(245, 242, 255, 0.5);
  font-size: 2rem;
  line-height: 1;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s ease;

  &:hover {
    color: #f5f2ff;
  }
`;

const ModalBody = styled.div`
  padding: 1.5rem 1.75rem;
`;

const ModalInfo = styled.p`
  margin: 0;
  font-size: 1rem;
  line-height: 1.6;
  color: rgba(245, 242, 255, 0.85);
`;

const ModalFooter = styled.div`
  padding: 0 1.75rem 1.5rem;
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
`;

const NightPhase: React.FC<NightPhaseProps> = ({
  language,
  players,
  playerRoles,
  judge,
  playersLeaved,
  playerFauls,
  nightCount,
  aliveDon,
  aliveSheriff,
  handledFouls,
  streamError = false,
  onMorning,
  onPlayerPunish,
  onPlayerFoulAction,
  onMarkFoulAsHandled,
  onDisqualifyPlayer,
  onPPK,
  onBackToDay,
  onTimeoutToggle,
  onShowRoles,
  onCancel,
  timerAccumulated,
  timerStartedAt,
  onTimerStateChange,
}) => {
  const [showGameActions, setShowGameActions] = useState(false);
  const [showPlayerPunishment, setShowPlayerPunishment] = useState<
    number | null
  >(null);
  const [pendingFoulDialogs, setPendingFoulDialogs] = useState<
    Array<{ playerIndex: number; foulCount: number }>
  >([]);
  const processingFoulRef = useRef(false);
  const [showRolesInTable, setShowRolesInTable] = useState(false);
  const [showCancelGameConfirm, setShowCancelGameConfirm] = useState(false);
  const [showBackToDayConfirm, setShowBackToDayConfirm] = useState(false);

  const dict = dictionaries[language].ph;

  const handleCancelGame = () => {
    setShowCancelGameConfirm(true);
  };

  const handleBackToDay = () => {
    setShowBackToDayConfirm(true);
  };

  const handleFoulAction = (
    action:
      | "add"
      | "reset"
      | "disqualify"
      | "penalty-today"
      | "penalty-tomorrow"
  ) => {
    if (showPlayerPunishment !== null) {
      onPlayerFoulAction(showPlayerPunishment, action);
      setShowPlayerPunishment(null);
    }
  };

  useEffect(() => {
    if (showPlayerPunishment !== null || pendingFoulDialogs.length === 0) {
      return;
    }

    const nextDialog = pendingFoulDialogs[0];
    setShowPlayerPunishment(nextDialog.playerIndex);
    onMarkFoulAsHandled(nextDialog.playerIndex, nextDialog.foulCount);
    setPendingFoulDialogs((prev) => prev.slice(1));
  }, [showPlayerPunishment, pendingFoulDialogs, onMarkFoulAsHandled]);

  useEffect(() => {
    for (let index = 0; index < playerFauls.length; index++) {
      const fouls = playerFauls[index];
      const foulKey = `${index}-${fouls}`;

      if ((fouls === 3 || fouls === 4) && !handledFouls.has(foulKey)) {
        const alreadyQueued = pendingFoulDialogs.some(
          (dialog) => dialog.playerIndex === index && dialog.foulCount === fouls
        );

        if (!alreadyQueued) {
          setPendingFoulDialogs((prev) => [
            ...prev,
            { playerIndex: index, foulCount: fouls },
          ]);
        }
      }
    }
  }, [playerFauls, handledFouls, pendingFoulDialogs]);

  const handlePlayerPunish = (playerIndex: number) => {
    if (processingFoulRef.current) {
      return;
    }

    processingFoulRef.current = true;

    const currentFouls = playerFauls[playerIndex] || 0;
    const newFoulCount = currentFouls + 1;

    onPlayerPunish(playerIndex);
    onPlayerFoulAction(playerIndex, "add");

    // Reset after a minimal delay to allow React to process the state update
    // This prevents double-clicks but doesn't block rapid consecutive foul additions
    setTimeout(() => {
      processingFoulRef.current = false;
    }, 100);

    if (newFoulCount === 3 || newFoulCount === 4) {
      const foulKey = `${playerIndex}-${newFoulCount}`;

      if (!handledFouls.has(foulKey)) {
        const alreadyQueued = pendingFoulDialogs.some(
          (dialog) =>
            dialog.playerIndex === playerIndex &&
            dialog.foulCount === newFoulCount
        );

        if (!alreadyQueued) {
          setPendingFoulDialogs((prev) => [
            ...prev,
            { playerIndex: playerIndex, foulCount: newFoulCount },
          ]);
        }
      }
    }
  };

  const handleNightActionsComplete = (actions: {
    shooting?: number;
    mafiaTarget: boolean;
    donCheck?: number;
    donMissed: boolean;
    sheriffCheck?: number;
    sheriffMissed: boolean;
  }) => {
    const nightActions: NightActionsData = {
      shooting: actions.shooting,
      mafiaTarget: actions.mafiaTarget,
      donCheck: actions.donCheck,
      sheriffCheck: actions.sheriffCheck,
      donMissed: actions.donMissed,
      sheriffMissed: actions.sheriffMissed,
    };

    onMorning(nightActions);
  };

  const isPlayerAlive = (playerIndex: number) => {
    return !playersLeaved.includes(playerIndex + 1);
  };

  const getPlayerRole = (playerIndex: number): GameRole => {
    return playerRoles[playerIndex] || "red";
  };

  const getRoleLabel = (role: GameRole) => {
    switch (role) {
      case "don":
        return dict.don;
      case "sheriff":
        return dict.shrf;
      case "black":
        return dict.maf;
      case "red":
        return dict.citizen;
      default:
        return dict.unknown;
    }
  };

  const getRoleVariant = (role: GameRole): RoleVariant => {
    switch (role) {
      case "don":
        return "don";
      case "sheriff":
        return "sheriff";
      case "black":
        return "black";
      case "red":
        return "red";
      default:
        return "none";
    }
  };

  const getMafiaPlayers = () => {
    return players
      .map((player, index) => ({ player, index, role: getPlayerRole(index) }))
      .filter(
        ({ role, index }) =>
          (role === "black" || role === "don") && isPlayerAlive(index)
      );
  };

  const renderPlayerRow = (player: Player, index: number) => {
    const playerNumber = index + 1;
    const isAlive = isPlayerAlive(index);
    const role = getPlayerRole(index);

    return (
      <PlayerRow
        key={player.user_id}
        id={`player${player.user_id}`}
        className={!isAlive ? "dead" : ""}
      >
        <NumberCell>{playerNumber}</NumberCell>
        <FoulCell>
          <FoulBadge onClick={() => handlePlayerPunish(index)}>
            {playerFauls[index] > 0 ? playerFauls[index] : "-"}
          </FoulBadge>
        </FoulCell>
        <td>
          <PlayerInfo>
            <PlayerAvatar src={player.avatar} alt={player.nickname} />
            <div>
              <PlayerName>{player.nickname}</PlayerName>
              {showRolesInTable && (
                <div style={{ marginTop: "0.25rem" }}>
                  <RoleTag $variant={getRoleVariant(role)}>
                    {getRoleLabel(role)}
                  </RoleTag>
                </div>
              )}
            </div>
          </PlayerInfo>
        </td>
      </PlayerRow>
    );
  };

  const aliveMafiaCount = getMafiaPlayers().length;

  return (
    <PhaseBackground>
      <PhaseContainer>
        <PhaseHeader
          title={dict.nph}
          variant="night"
          language={language}
          meta={`#${String(nightCount).padStart(2, "0")}`}
          streamError={streamError}
          onTimeoutToggle={onTimeoutToggle}
          timerAccumulated={timerAccumulated}
          timerStartedAt={timerStartedAt}
          onTimerStateChange={onTimerStateChange}
          actions={[
            {
              id: "spotify",
              label: "♫",
              onClick: () => {
                window.location.href = "spotify:";
              },
              variant: "secondary",
            },
            {
              id: "back-to-day",
              label: dict.btday,
              onClick: handleBackToDay,
              variant: "secondary",
            },
          ]}
        />

        <PhaseMain>
          <PhaseStack>
            <SectionCard>
              <NightActions
                language={language}
                players={players}
                playersLeaved={playersLeaved}
                playerRoles={playerRoles}
                aliveDon={aliveDon}
                aliveSheriff={aliveSheriff}
                onComplete={handleNightActionsComplete}
              />
            </SectionCard>

            <PlayersTable>
              <thead>
                <tr>
                  <th>#</th>
                  <th>{dict.fls}</th>
                  <th>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <span>{dict.player}</span>
                      <RolesToggle
                        onClick={() => setShowRolesInTable(!showRolesInTable)}
                      >
                        👁️ {dict.roles || "РОЛИ"}
                      </RolesToggle>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {players.map((player, index) => {
                  if (!isPlayerAlive(index)) return null;
                  return renderPlayerRow(player, index);
                })}
              </tbody>
            </PlayersTable>

            <SectionCard>
              <ActionBar>
                <PhaseButton
                  $variant="secondary"
                  onClick={() => setShowGameActions(true)}
                >
                  ⚖️ {dict.actions}
                </PhaseButton>
                <PhaseButton $variant="danger" onClick={handleCancelGame}>
                  {dict.cancelgame}
                </PhaseButton>
              </ActionBar>
            </SectionCard>

            <MetaCard>
              <MetaInfo></MetaInfo>
            </MetaCard>
          </PhaseStack>
        </PhaseMain>

        {showPlayerPunishment !== null && (
          <PlayerPunishment
            language={language}
            player={players[showPlayerPunishment]}
            playerIndex={showPlayerPunishment}
            currentFouls={playerFauls[showPlayerPunishment] || 0}
            onFoulAction={handleFoulAction}
          />
        )}

        {showGameActions && (
          <GameActions
            language={language}
            players={players}
            playerRoles={playerRoles}
            playersLeaved={playersLeaved}
            onDisqualifyPlayer={onDisqualifyPlayer}
            onPPK={onPPK}
            onClose={() => setShowGameActions(false)}
          />
        )}

        {showBackToDayConfirm && (
          <ModalOverlay>
            <ModalCard>
              <ModalHeader>
                <ModalTitle>⚠️ {dict.confirm_back_to_day}</ModalTitle>
                <ModalCloseButton onClick={() => setShowBackToDayConfirm(false)}>
                  ×
                </ModalCloseButton>
              </ModalHeader>

              <ModalBody>
                <ModalInfo>
                  {dict.confirm_back_to_day_message}
                </ModalInfo>
              </ModalBody>

              <ModalFooter>
                <PhaseButton 
                  $variant="secondary" 
                  onClick={() => setShowBackToDayConfirm(false)}
                >
                  {dict.cncl}
                </PhaseButton>
                <PhaseButton 
                  $variant="primary" 
                  onClick={() => {
                    setShowBackToDayConfirm(false);
                    onBackToDay();
                  }}
                >
                  {dict.yes_return}
                </PhaseButton>
              </ModalFooter>
            </ModalCard>
          </ModalOverlay>
        )}

        {showCancelGameConfirm && (
          <ModalOverlay>
            <ModalCard>
              <ModalHeader>
                <ModalTitle>⚠️ {dict.confirm_cancel_game}</ModalTitle>
                <ModalCloseButton onClick={() => setShowCancelGameConfirm(false)}>
                  ×
                </ModalCloseButton>
              </ModalHeader>

              <ModalBody>
                <ModalInfo>
                  {dict.confirm_cancel_game_message}
                </ModalInfo>
              </ModalBody>

              <ModalFooter>
                <PhaseButton 
                  $variant="secondary" 
                  onClick={() => setShowCancelGameConfirm(false)}
                >
                  {dict.cncl}
                </PhaseButton>
                <PhaseButton 
                  $variant="danger" 
                  onClick={() => {
                    setShowCancelGameConfirm(false);
                    onCancel();
                  }}
                >
                  {dict.yes_cancel}
                </PhaseButton>
              </ModalFooter>
            </ModalCard>
          </ModalOverlay>
        )}
      </PhaseContainer>
    </PhaseBackground>
  );
};

export default NightPhase;
